import { apiGet, apiPost } from "./fetch";

/** Who a release-notes folder was written for. */
export type ReleaseNotesAudience = "staff" | "all";

/** One published release-notes folder, as the running image carries it. */
export interface ReleaseNote {
	/** The folder name under `releasenotes/`, e.g. `2026-08-25-release-notes`. */
	folder: string;
	/** When the deploy that carried it went out; null for a folder that
	 * shipped before deploys were recorded. Such a folder never badges. */
	published_at: string | null;
	/** The workflow ticket that authored it; null for a manual release. */
	ticket: number | null;
	audience: ReleaseNotesAudience;
	content_en: string;
	content_sv: string;
	/** Image filenames the markdown references, served by `releaseNoteImageUrl`. */
	images: string[];
}

export interface ReleaseNotesPayload {
	notes: ReleaseNote[];
	/** When this reader last opened the page; null if they never have. */
	seen_at: string | null;
}

export function fetchReleaseNotes(): Promise<ReleaseNotesPayload> {
	return apiGet("/api/v2/release-notes/") as Promise<ReleaseNotesPayload>;
}

/** Stamp the reader's watermark — what clears the menu badge. */
export function markReleaseNotesSeen(): Promise<{ seen_at: string }> {
	return apiPost("/api/v2/release-notes/mark_seen/", {}) as Promise<{
		seen_at: string;
	}>;
}

/** The authenticated endpoint serving one image from a published folder. */
export function releaseNoteImageUrl(folder: string, filename: string): string {
	return `/api/v2/release-notes/${folder}/images/${filename}/`;
}

/**
 * How many published notes the reader has not opened the page since.
 *
 * The watermark and the publication dates are the only inputs, so the badge
 * and the page always agree — and a folder with no publication row (deployed
 * before the deploy command existed) never counts.
 */
export function countUnseenReleaseNotes(payload: ReleaseNotesPayload): number {
	const seenAt = payload.seen_at === null ? null : Date.parse(payload.seen_at);
	return payload.notes.filter(
		(note) =>
			note.published_at !== null &&
			(seenAt === null || Date.parse(note.published_at) > seenAt)
	).length;
}
